import { useState, useRef, useEffect } from 'react';
import PortalModal from '../common/PortalModal';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Typography, CircularProgress, Card, CardMedia, CardContent } from '@mui/material';
import {
  saveIsAutomatedJobCompleted,
  saveIsCheckingAutomatedJobStatus,
  saveProcessingJobs,
} from './actions';
import { SAM_STATUS_POLLING_PREDELAY } from '../common/constants/sam';
import { startJobPolling } from './utils';
import { ERROR_CODE } from '../common/constants';
import { toast } from 'react-toastify';
import { postData } from '../../DataAccessLayer';
import _ from 'lodash';

export const JobTriggerCard = ({ id, jobName = '', action = '', triggerFunction = () => {} }) => {
  const pollingStopFnRef = useRef(null);

  const dispatch = useDispatch();

  const { isAutomatedJobCompleted, isCheckingAutomatedJobStatus } = useSelector(
    (state) => state.samReducer,
  );

  const [isJobConfirmationModalOpen, setIsJobConfirmationModalOpen] = useState(false);

  // If parent component already polling, we dont need to poll to avoid unnecessary poll
  useEffect(() => {
    // cleanup on unmount
    return () => {
      if (pollingStopFnRef.current) {
        pollingStopFnRef.current();
      }
    };
  }, []);

  const toggleJobConfirmationModal = () => {
    setIsJobConfirmationModalOpen(!isJobConfirmationModalOpen);
  };

  const onTriggeredAutomatedUnassigmentJob = async () => {
    try {
      toggleJobConfirmationModal();
      dispatch(saveIsAutomatedJobCompleted(false));

      await postData(triggerFunction(), {}, true);

      toast.success(`Successfully started automated ${jobName} job.`);
    } catch (err) {
      console.log(err);
      // In the backend we check if the auto job is running already at the time of triggerring this request
      // It will return 400 Bad request if the job is runnning so we can handle it in the FE
      if (err.status === ERROR_CODE.BAD_REQUEST) {
        dispatch(saveIsAutomatedJobCompleted(false));
      }

      toast.error(`Unable to start automated ${jobName} job.`);
    } finally {
      // always stop any existing polling
      pollingStopFnRef.current?.();

      pollingStopFnRef.current = await startJobPolling({
        isAutomatedJobCompleted,
        saveIsAutomatedJobCompleted: (isAutomatedJobCompleted) =>
          dispatch(saveIsAutomatedJobCompleted(isAutomatedJobCompleted)),
        saveIsCheckingAutomatedJobStatus: (isCheckingAutomatedJobStatus) =>
          dispatch(saveIsCheckingAutomatedJobStatus(isCheckingAutomatedJobStatus)),
        saveProcessingJobs: (processingJobs) => dispatch(saveProcessingJobs(processingJobs)),
        preDelay: SAM_STATUS_POLLING_PREDELAY,
      });
    }
  };

  return (
    <div className={`myOdu__${id} myOdu__sam`}>
      <PortalModal
        title={`Automatic ${jobName} Job Confirmation`}
        maxWidth="sm"
        isOpen={isJobConfirmationModalOpen}
        onClose={toggleJobConfirmationModal}
        isCloseable
        primaryButtonTitle="Yes, Start Job"
        secondaryButtonTitle="No, Cancel"
        onPrimaryClick={onTriggeredAutomatedUnassigmentJob}
        onSecondaryClick={toggleJobConfirmationModal}
      >
        <Typography component="p">
          {`This job will automatically ${_.toLower(action)}. Would you like to
                continue?`}
        </Typography>
      </PortalModal>

      <Card elevation={0}>
        <CardMedia
          className="media"
          component="img"
          image="/img/gears.svg"
          alt="person using gears"
        />
        <Typography component="h3" align="center">
          {action}
        </Typography>
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Button
            variant="outlined"
            className="myOdu__button secondary"
            onClick={() => toggleJobConfirmationModal()}
            disabled={!isAutomatedJobCompleted || isCheckingAutomatedJobStatus}
          >
            {!isAutomatedJobCompleted && !isCheckingAutomatedJobStatus ? (
              <>
                Job is in progress
                <CircularProgress className="circularProgress" />
              </>
            ) : (
              `Run Automated ${action} Job`
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
